import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Calendar, Tag, ExternalLink, Loader2, AlertCircle, ArrowRight, Globe, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design system: ONLY Red + Blue shades
const reportTypeConfig = {
  "Annual Report": { color: "bg-[#991B1B]/10 text-[#991B1B] border-[#991B1B]/20", icon: Globe },
  "Quarterly Briefing": { color: "bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20", icon: BarChart3 },
  "Monthly Signals": { color: "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20", icon: FileText },
  "Special Publication": { color: "bg-[#60A5FA]/10 text-[#60A5FA] border-[#60A5FA]/20", icon: FileText }
};

export default function ObservatoryReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/observatory/reports`);
      setReports(response.data.reports);
      setLoading(false);
    } catch (err) {
      console.error("Reports fetch error:", err);
      setError("Failed to load reports");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F172A]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Card className="max-w-md border-[#E5E7EB]">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-[#111827] mb-2">Error Loading Reports</h2>
            <p className="text-[#4B5563] mb-4">{error}</p>
            <Button onClick={fetchReports}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const annualReports = reports.filter(r => r.report_type === "Annual Report");
  const quarterlyReports = reports.filter(r => r.report_type === "Quarterly Briefing");
  const monthlyReports = reports.filter(r => r.report_type === "Monthly Signals");
  const specialReports = reports.filter(r => r.report_type === "Special Publication");

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 sm:py-12" data-testid="reports-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm font-medium text-[#991B1B] uppercase tracking-wider">Research Archive</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#991B1B] mt-2 mb-4" data-testid="reports-title">
            Observatory Reports
          </h1>
          <p className="text-[#4B5563] max-w-2xl mx-auto">
            Access the Annual Global Workforce Intelligence Report, Quarterly Briefings, Monthly Signals, and special research publications.
          </p>
        </motion.div>

        {/* Annual Reports */}
        {annualReports.length > 0 && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-3 h-3 rounded-full bg-[#991B1B]"></span>
              <h2 className="font-heading text-2xl font-semibold text-[#991B1B]">Annual Global Workforce Intelligence Report</h2>
            </div>
            <div className="space-y-4">
              {annualReports.map((report) => (
                <ReportCard key={report.id} report={report} featured />
              ))}
            </div>
          </motion.section>
        )}

        {/* Quarterly Briefings */}
        {quarterlyReports.length > 0 && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-3 h-3 rounded-full bg-[#1E3A8A]"></span>
              <h2 className="font-heading text-2xl font-semibold text-[#991B1B]">Quarterly Workforce Intelligence Briefing</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {quarterlyReports.map((report) => (
                <ReportCard key={report.id} report={report} />
              ))}
            </div>
          </motion.section>
        )}

        {/* Monthly Signals */}
        {monthlyReports.length > 0 && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-3 h-3 rounded-full bg-[#3B82F6]"></span>
              <h2 className="font-heading text-2xl font-semibold text-[#991B1B]">Monthly Workforce Intelligence Signals</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {monthlyReports.map((report) => (
                <ReportCard key={report.id} report={report} compact />
              ))}
            </div>
          </motion.section>
        )}

        {/* Special Publications */}
        {specialReports.length > 0 && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-3 h-3 rounded-full bg-[#60A5FA]"></span>
              <h2 className="font-heading text-2xl font-semibold text-[#991B1B]">Special Publications</h2>
            </div>
            <div className="space-y-4">
              {specialReports.map((report) => (
                <ReportCard key={report.id} report={report} />
              ))}
            </div>
          </motion.section>
        )}

        {/* Empty State */}
        {reports.length === 0 && (
          <Card className="border-[#E5E7EB] observatory-card" data-testid="no-reports">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-[#E5E7EB] mx-auto mb-6" />
              <h2 className="font-heading text-2xl font-semibold text-[#111827] mb-3">
                No Reports Available Yet
              </h2>
              <p className="text-[#4B5563]">
                Research reports will be published here as data accumulates from diagnostic submissions.
              </p>
            </CardContent>
          </Card>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Card className="border-[#E5E7EB] observatory-card bg-white" data-testid="cta-section">
            <CardContent className="p-8 sm:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-[#991B1B] mb-3">
                    Contribute to Research
                  </h3>
                  <p className="text-[#4B5563] mb-6">
                    Complete the Workforce Intelligence Diagnostic to contribute to the Global Workforce Intelligence Index and receive your personalized results.
                  </p>
                  <Link to="/juswork/observatory/survey">
                    <Button className="gap-2 bg-[#991B1B] text-white hover:bg-[#7F1D1D]" data-testid="take-diagnostic-cta">
                      Take the Diagnostic
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E5E7EB]">
                    <Globe className="w-8 h-8 text-[#991B1B]" />
                    <div>
                      <p className="font-medium text-[#111827] text-sm">Annual Global Report</p>
                      <p className="text-xs text-[#4B5563]">Comprehensive yearly analysis</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E5E7EB]">
                    <BarChart3 className="w-8 h-8 text-[#1E3A8A]" />
                    <div>
                      <p className="font-medium text-[#111827] text-sm">Quarterly Briefing</p>
                      <p className="text-xs text-[#4B5563]">Industry trends and updates</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E5E7EB]">
                    <FileText className="w-8 h-8 text-[#3B82F6]" />
                    <div>
                      <p className="font-medium text-[#111827] text-sm">Monthly Signals</p>
                      <p className="text-xs text-[#4B5563]">Emerging patterns and indicators</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

function ReportCard({ report, compact = false, featured = false }) {
  const config = reportTypeConfig[report.report_type] || reportTypeConfig["Special Publication"];
  const IconComponent = config.icon;
  
  return (
    <Card 
      className={`border-[#E5E7EB] observatory-card hover:shadow-lg transition-shadow duration-300 ${featured ? 'border-l-4 border-l-[#991B1B]' : ''}`}
      data-testid={`report-card-${report.id}`}
    >
      <CardContent className={compact ? "p-5" : "p-6"}>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-shrink-0">
            <div className={`w-12 h-12 rounded-lg ${config.color.split(' ')[0]} flex items-center justify-center`}>
              <IconComponent className={`w-6 h-6 ${config.color.split(' ')[1]}`} />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge variant="outline" className={config.color}>
                <Tag className="w-3 h-3 mr-1" />
                {report.report_type}
              </Badge>
              <span className="text-xs text-[#4B5563] flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(report.published_date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
            
            <h3 className="font-heading text-lg font-semibold text-[#111827] mb-2">
              {report.title}
            </h3>
            
            <p className={`text-[#4B5563] text-sm ${compact ? 'line-clamp-2' : ''}`}>
              {compact ? report.description : report.summary}
            </p>
          </div>
          
          <div className="flex-shrink-0 self-center">
            {report.download_url ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 border-[#E5E7EB]"
                data-testid={`download-${report.id}`}
                onClick={() => window.open(report.download_url, '_blank')}
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 text-[#4B5563] border-[#E5E7EB]"
                disabled
                data-testid={`coming-soon-${report.id}`}
              >
                <ExternalLink className="w-4 h-4" />
                Coming Soon
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
